import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getAllProductsUnpaginated, getAllOrders, getAllCustomers, getDashboardSummary } from '../services/api';
import ExcelJS from 'exceljs';
import './Dashboard.css';

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   HELPERS
   â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
var formatINR = function (value) {
  return '\u20B9' + (Number(value) || 0).toLocaleString('en-IN');
};

var toDateStr = function (d) {
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
};

var getDefaultSummaryRange = function () {
  var today = new Date();
  var todayStr = toDateStr(today);
  return { from: todayStr, to: todayStr };
};

var startOfDay = function (dateStr) { return new Date(dateStr + 'T00:00:00.000'); };
var endOfDay = function (dateStr) { return new Date(dateStr + 'T23:59:59.999'); };

var extractEntityArray = function (res, preferredKeys) {
  if (!res || !res.data) return [];
  var payload = res.data;
  var sources = [payload];
  if (payload && typeof payload === 'object') {
    if (payload.data !== undefined) sources.push(payload.data);
    if (payload.result !== undefined) sources.push(payload.result);
    if (payload.results !== undefined) sources.push(payload.results);
  }
  for (var i = 0; i < sources.length; i += 1) {
    var source = sources[i];
    if (Array.isArray(source)) return source.filter(isEntityActive);
    if (!source || typeof source !== 'object') continue;
    for (var j = 0; j < preferredKeys.length; j += 1) {
      var key = preferredKeys[j];
      if (Array.isArray(source[key])) return source[key].filter(isEntityActive);
    }
  }
  return [];
};

var extractProductArray = function (res) { return extractEntityArray(res, ['products']); };
var extractOrderArray = function (res) { return extractEntityArray(res, ['orders', 'orderList']); };
var extractCustomerArray = function (res) { return extractEntityArray(res, ['customers', 'users']); };
var normalizeValue = function (value) { return String(value || '').toLowerCase().trim(); };
var isEntityActive = function (item) {
  if (!item || typeof item !== 'object') return false;
  return item.isDeleted !== true && item.deletedAt == null;
};

var parseAmount = function (value) {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  var text = String(value || '').trim();
  if (!text) return 0;
  text = text.replace(/[\u20B9,\s]/g, '');
  var num = Number(text);
  return Number.isNaN(num) ? 0 : num;
};

var getOrderTotal = function (order) {
  if (!order) return 0;
  var value = parseAmount(
    order.finalAmount || order.payableAmount || order.totalPayable || order.totalAmount || order.total || order.amount || order.orderTotal || order.grandTotal ||
    order.order_amount || order.grand_total || order.cartTotal || order.subTotal || order.order_total || 0
  );
  if (value > 0) return value;
  var items = getOrderItems(order);
  if (!items.length) return 0;
  return items.reduce(function (sum, item) {
    return sum + getItemQuantity(item) * parseAmount(getItemUnitPrice(item));
  }, 0);
};

var getOrderStatus = function (order) {
  if (!order) return '';
  return String(order.orderStatus || order.status || order.order_status || '').toLowerCase().trim();
};

var getOrderDate = function (order) {
  if (!order) return null;
  var raw = order.createdAt || order.created_at || order.orderDate || order.order_date || order.date || order.paidAt || order.paymentDate || order.updatedAt || order.updated_at;
  if (!raw) return null;
  var d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

var EXCLUDED_STATUSES = ['cancelled', 'canceled', 'rejected', 'failed', 'refunded'];

var isPaidOrCompletedOrder = function (order) {
  if (!order || order.isDeleted) return false;
  var orderStatus = normalizeValue(order.orderStatus || order.status || '');
  var paymentStatus = normalizeValue(order.paymentStatus || (order.payment && order.payment.status) || order.payment_state || '');
  if (EXCLUDED_STATUSES.indexOf(orderStatus) !== -1) return false;
  if (EXCLUDED_STATUSES.indexOf(paymentStatus) !== -1) return false;
  if (paymentStatus === 'paid' || paymentStatus === 'success' || paymentStatus === 'completed') return true;
  var activeOrderStatuses = ['pending', 'processing', 'confirmed', 'accepted', 'shipped', 'out_for_delivery', 'out for delivery', 'delivered', 'completed'];
  if (activeOrderStatuses.indexOf(orderStatus) !== -1) return true;
  var paymentMethod = normalizeValue(order.paymentMethod || order.paymentType || '');
  if (paymentMethod === 'cod') return true;
  if (getOrderTotal(order) > 0) return true;
  return false;
};

var getOrderItems = function (order) {
  if (!order || typeof order !== 'object') return [];
  var items = order.items || order.products || order.orderItems || order.order_items || order.order_products ||
    order.lineItems || order.line_items || order.itemsList || order.items_list || [];
  if (!Array.isArray(items) && order.data) {
    if (Array.isArray(order.data.items)) items = order.data.items;
    else if (Array.isArray(order.data.products)) items = order.data.products;
  }
  return Array.isArray(items) ? items : [];
};

var getItemQuantity = function (item) {
  if (!item || typeof item !== 'object') return 1;
  var qty = Number(item.quantity || item.qty || item.count || item.quantityOrdered || item.qtyOrdered || 1);
  return Number.isNaN(qty) || qty <= 0 ? 1 : qty;
};

var getItemUnitPrice = function (item) {
  if (!item) return 0;
  var product = item.product || item.productId || {};
  var price = Number(item.unitPrice || item.price || item.sellingPrice || product.sellingPrice || product.salePrice || product.price || 0);
  return Number.isNaN(price) ? 0 : price;
};

var getProductMeta = function (item) {
  var product = (item && (item.product || item.productId)) || {};
  var productName = product && typeof product === 'object'
    ? (product.name || product.productName || product.title || product.productTitle) : product;
  var name = productName || item.name || item.productName || item.title || item.productTitle || item.product || item.productId || 'Product';
  return {
    id: (product && typeof product === 'object' && (product._id || product.id)) ||
      (typeof item.productId === 'string' ? item.productId : null) || item._id || String(name || 'unknown'),
    name: String(name || 'Product'),
  };
};

var filterOrdersByRange = function (orders, startStr, endStr) {
  if (!startStr && !endStr) return orders;
  var start = startStr ? startOfDay(startStr) : null;
  var end = endStr ? endOfDay(endStr) : null;
  return (orders || []).filter(function (order) {
    var d = getOrderDate(order);
    if (!d) return false;
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  });
};

var buildDailySeries = function (orders, days, offsetDays) {
  var now = new Date();
  var rows = [];
  var totalsByKey = {};
  var offset = Number(offsetDays) || 0;
  for (var i = days - 1; i >= 0; i -= 1) {
    var d = new Date(now);
    d.setHours(0, 0, 0, 0);
    d.setDate(now.getDate() - i - offset);
    var key = d.toISOString().slice(0, 10);
    totalsByKey[key] = 0;
    rows.push({
      key: key,
      day: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      weekDay: d.toLocaleDateString('en-IN', { weekday: 'short' }),
      value: 0,
    });
  }
  (orders || []).forEach(function (order) {
    if (!isPaidOrCompletedOrder(order)) return;
    var date = getOrderDate(order);
    if (!date) return;
    var istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    var normalized = new Date(istDate.getFullYear(), istDate.getMonth(), istDate.getDate());
    var key = normalized.toISOString().slice(0, 10);
    if (Object.prototype.hasOwnProperty.call(totalsByKey, key)) {
      totalsByKey[key] += getOrderTotal(order);
    }
  });
  return rows.map(function (r) { return { key: r.key, day: r.day, weekDay: r.weekDay, value: totalsByKey[r.key] || 0 }; });
};

var buildWeeklySeries = function (orders, days, offsetDays) {
  var now = new Date();
  var offset = Number(offsetDays) || 0;
  var numWeeks = Math.ceil(days / 7);
  var rows = [];
  for (var i = numWeeks - 1; i >= 0; i -= 1) {
    var weekEnd = new Date(now);
    weekEnd.setHours(0, 0, 0, 0);
    weekEnd.setDate(now.getDate() - (i * 7) - offset);
    var weekStart = new Date(weekEnd);
    weekStart.setDate(weekEnd.getDate() - 6);
    rows.push({
      key: weekStart.toISOString().slice(0, 10),
      day: weekStart.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + '\u2013' +
        weekEnd.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      weekDay: '',
      value: 0,
      _start: weekStart,
      _end: weekEnd,
    });
  }
  (orders || []).forEach(function (order) {
    if (!isPaidOrCompletedOrder(order)) return;
    var date = getOrderDate(order);
    if (!date) return;
    var istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    var normalized = new Date(istDate.getFullYear(), istDate.getMonth(), istDate.getDate());
    for (var k = 0; k < rows.length; k += 1) {
      if (normalized >= rows[k]._start && normalized <= rows[k]._end) {
        rows[k].value += getOrderTotal(order);
        break;
      }
    }
  });
  return rows.map(function (r) { return { key: r.key, day: r.day, weekDay: r.weekDay, value: r.value }; });
};

var buildMonthlySeries = function (orders, days, offsetDays) {
  var now = new Date();
  var offset = Number(offsetDays) || 0;
  var numMonths = Math.ceil(days / 30);
  var baseMonth = now.getMonth() - Math.floor(offset / 30);
  var baseYear = now.getFullYear();
  while (baseMonth < 0) { baseMonth += 12; baseYear -= 1; }
  var rows = [];
  for (var i = numMonths - 1; i >= 0; i -= 1) {
    var d = new Date(baseYear, baseMonth - i, 1);
    rows.push({
      key: d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'),
      day: d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
      weekDay: '',
      value: 0,
      _year: d.getFullYear(),
      _month: d.getMonth(),
    });
  }
  (orders || []).forEach(function (order) {
    if (!isPaidOrCompletedOrder(order)) return;
    var date = getOrderDate(order);
    if (!date) return;
    var istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    for (var k = 0; k < rows.length; k += 1) {
      if (istDate.getFullYear() === rows[k]._year && istDate.getMonth() === rows[k]._month) {
        rows[k].value += getOrderTotal(order);
        break;
      }
    }
  });
  return rows.map(function (r) { return { key: r.key, day: r.day, weekDay: r.weekDay, value: r.value }; });
};

var buildRevenueSeries = function (orders, days, offsetDays) {
  if (days <= 90) return buildDailySeries(orders, days, offsetDays);
  if (days <= 180) return buildWeeklySeries(orders, days, offsetDays);
  return buildMonthlySeries(orders, days, offsetDays);
};

var buildDailyOrderCounts = function (orders, days, offsetDays) {
  var now = new Date();
  var rows = [];
  var countsByKey = {};
  var offset = Number(offsetDays) || 0;
  for (var i = days - 1; i >= 0; i -= 1) {
    var d = new Date(now);
    d.setHours(0, 0, 0, 0);
    d.setDate(now.getDate() - i - offset);
    var key = d.toISOString().slice(0, 10);
    countsByKey[key] = 0;
    rows.push({ key: key, day: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }), value: 0 });
  }
  (orders || []).forEach(function (order) {
    if (!isPaidOrCompletedOrder(order)) return;
    var date = getOrderDate(order);
    if (!date) return;
    var istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    var key = new Date(istDate.getFullYear(), istDate.getMonth(), istDate.getDate()).toISOString().slice(0, 10);
    if (Object.prototype.hasOwnProperty.call(countsByKey, key)) countsByKey[key] += 1;
  });
  return rows.map(function (r) { return { key: r.key, day: r.day, value: countsByKey[r.key] || 0 }; });
};

var buildWeeklyOrderCounts = function (orders, days, offsetDays) {
  var now = new Date();
  var offset = Number(offsetDays) || 0;
  var numWeeks = Math.ceil(days / 7);
  var rows = [];
  for (var i = numWeeks - 1; i >= 0; i -= 1) {
    var weekEnd = new Date(now);
    weekEnd.setHours(0, 0, 0, 0);
    weekEnd.setDate(now.getDate() - (i * 7) - offset);
    var weekStart = new Date(weekEnd);
    weekStart.setDate(weekEnd.getDate() - 6);
    rows.push({
      key: weekStart.toISOString().slice(0, 10),
      day: weekStart.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + '\u2013' + weekEnd.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      value: 0, _start: weekStart, _end: weekEnd,
    });
  }
  (orders || []).forEach(function (order) {
    if (!isPaidOrCompletedOrder(order)) return;
    var date = getOrderDate(order);
    if (!date) return;
    var istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    var normalized = new Date(istDate.getFullYear(), istDate.getMonth(), istDate.getDate());
    for (var k = 0; k < rows.length; k += 1) {
      if (normalized >= rows[k]._start && normalized <= rows[k]._end) { rows[k].value += 1; break; }
    }
  });
  return rows.map(function (r) { return { key: r.key, day: r.day, value: r.value }; });
};

var buildMonthlyOrderCounts = function (orders, days, offsetDays) {
  var now = new Date();
  var offset = Number(offsetDays) || 0;
  var numMonths = Math.ceil(days / 30);
  var baseMonth = now.getMonth() - Math.floor(offset / 30);
  var baseYear = now.getFullYear();
  while (baseMonth < 0) { baseMonth += 12; baseYear -= 1; }
  var rows = [];
  for (var i = numMonths - 1; i >= 0; i -= 1) {
    var d = new Date(baseYear, baseMonth - i, 1);
    rows.push({ key: d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'), day: d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }), value: 0, _year: d.getFullYear(), _month: d.getMonth() });
  }
  (orders || []).forEach(function (order) {
    if (!isPaidOrCompletedOrder(order)) return;
    var date = getOrderDate(order);
    if (!date) return;
    var istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    for (var k = 0; k < rows.length; k += 1) {
      if (istDate.getFullYear() === rows[k]._year && istDate.getMonth() === rows[k]._month) { rows[k].value += 1; break; }
    }
  });
  return rows.map(function (r) { return { key: r.key, day: r.day, value: r.value }; });
};

var buildOrderCountSeries = function (orders, days, offsetDays) {
  if (days <= 90) return buildDailyOrderCounts(orders, days, offsetDays);
  if (days <= 180) return buildWeeklyOrderCounts(orders, days, offsetDays);
  return buildMonthlyOrderCounts(orders, days, offsetDays);
};

var buildTopProducts = function (orders) {
  var map = {};
  (orders || []).forEach(function (order) {
    if (!isPaidOrCompletedOrder(order)) return;
    getOrderItems(order).forEach(function (item) {
      if (!item) return;
      var meta = getProductMeta(item);
      var qty = getItemQuantity(item);
      var revenue = parseAmount(item.total || item.totalPrice || item.amount || item.orderTotal || item.grandTotal || item.subTotal || item.price || item.unitPrice || 0);
      if (!revenue) revenue = qty * getItemUnitPrice(item);
      var key = String(meta.id || meta.name || 'unknown');
      if (!map[key]) map[key] = { n: meta.name, qty: 0, revenue: 0 };
      map[key].qty += qty;
      map[key].revenue += revenue;
    });
  });
  var rows = Object.keys(map).map(function (k) { return map[k]; });
  rows.sort(function (a, b) {
    if (b.qty !== a.qty) return b.qty - a.qty;
    return b.revenue - a.revenue;
  });
  return rows.slice(0, 5).map(function (row, idx) {
    return {
      r: String(idx + 1),
      rc: idx === 0 ? 'gold' : idx === 1 ? 'silver' : idx === 2 ? 'bronze' : '',
      n: row.n,
      c: row.qty + ' sold',
      v: formatINR(row.revenue)
    };
  });
};

var buildExportRows = function (orders, startStr, endStr) {
  var filtered = filterOrdersByRange(orders, startStr, endStr);
  if (!filtered.length) return [];
  return filtered.map(function (order) {
    var d = getOrderDate(order);
    return {
      Date: d ? d.toLocaleDateString('en-IN') : '',
      OrderID: order._id || order.id || '',
      Status: order.orderStatus || order.status || '',
      PaymentStatus: order.paymentStatus || '',
      Total: getOrderTotal(order),
    };
  });
};

var downloadExcel = function (rows, filename) {
  var workbook = new ExcelJS.Workbook();
  var sheet = workbook.addWorksheet('Orders');
  var columns = ['Date', 'OrderID', 'Status', 'PaymentStatus', 'Total'];
  sheet.columns = columns.map(function (key) { return { header: key, key: key, width: key === 'OrderID' ? 26 : 18 }; });
  rows.forEach(function (row) { sheet.addRow(row); });
  sheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
  sheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF061A38' } };
  return workbook.xlsx.writeBuffer().then(function (buffer) {
    var blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url; link.download = filename; document.body.appendChild(link); link.click();
    document.body.removeChild(link); URL.revokeObjectURL(url);
  });
};

var WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

var buildCalendarCells = function (year, month) {
  var firstDay = new Date(year, month, 1).getDay();
  var daysInMonth = new Date(year, month + 1, 0).getDate();
  var cells = [];
  for (var i = 0; i < firstDay; i += 1) cells.push(null);
  for (var d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);
  return cells;
};



/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   LEAF BACKGROUND COMPONENT
   â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
var LeafBackground = function () {
  var e = React.createElement;

  var defs = e('defs', null,
    e('linearGradient', { id: 'lg-leaf-dark', x1: '0%', y1: '0%', x2: '100%', y2: '100%' },
      e('stop', { offset: '0%', stopColor: '#1a6b5a' }),
      e('stop', { offset: '55%', stopColor: '#2d8c6e' }),
      e('stop', { offset: '100%', stopColor: '#3aaa82' })
    ),
    e('linearGradient', { id: 'lg-leaf-mid', x1: '0%', y1: '0%', x2: '100%', y2: '100%' },
      e('stop', { offset: '0%', stopColor: '#2e9c74' }),
      e('stop', { offset: '100%', stopColor: '#55c89a' })
    ),
    e('linearGradient', { id: 'lg-leaf-light', x1: '0%', y1: '0%', x2: '100%', y2: '100%' },
      e('stop', { offset: '0%', stopColor: '#4db888' }),
      e('stop', { offset: '100%', stopColor: '#7ddbb0' })
    )
  );

  var leaf1 = e('svg', {
    className: 'dash-leaf dash-leaf--1',
    viewBox: '0 0 200 320',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': 'true'
  },
    defs,
    e('path', { fill: 'url(#lg-leaf-dark)', d: 'M100 300 C60 260 20 200 10 140 C0 80 30 30 80 10 C90 6 100 4 110 6 C140 14 170 50 180 100 C195 160 175 240 140 280 Z' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.22)', strokeWidth: '3', strokeLinecap: 'round', d: 'M100 300 C100 240 98 170 95 100 C92 50 88 20 80 10' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.14)', strokeWidth: '1.5', strokeLinecap: 'round', d: 'M97 240 C80 230 55 220 30 215' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.14)', strokeWidth: '1.5', strokeLinecap: 'round', d: 'M98 200 C115 192 138 185 160 180' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.14)', strokeWidth: '1.5', strokeLinecap: 'round', d: 'M96 160 C78 148 52 138 28 132' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.14)', strokeWidth: '1.5', strokeLinecap: 'round', d: 'M97 120 C112 110 132 100 155 94' }),
    e('ellipse', { cx: '85', cy: '90', rx: '22', ry: '50', fill: 'rgba(255,255,255,0.08)', transform: 'rotate(-18 85 90)' })
  );

  var leaf2 = e('svg', { className: 'dash-leaf dash-leaf--2', viewBox: '0 0 160 240', xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': 'true' },
    e('defs', null, e('linearGradient', { id: 'lg2-a', x1: '0%', y1: '0%', x2: '100%', y2: '100%' }, e('stop', { offset: '0%', stopColor: '#2d8c6e' }), e('stop', { offset: '100%', stopColor: '#5bbf93' }))),
    e('path', { fill: 'url(#lg2-a)', d: 'M80 230 C50 195 15 155 8 110 C1 65 25 22 65 8 C72 5 80 4 90 6 C118 14 142 48 148 90 C158 140 138 190 108 218 Z' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.18)', strokeWidth: '2.5', strokeLinecap: 'round', d: 'M80 230 C79 175 77 120 75 75 C72 40 68 16 65 8' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.12)', strokeWidth: '1.2', strokeLinecap: 'round', d: 'M77 185 C62 175 42 168 20 163' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.12)', strokeWidth: '1.2', strokeLinecap: 'round', d: 'M77 150 C92 142 110 136 130 132' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.12)', strokeWidth: '1.2', strokeLinecap: 'round', d: 'M76 115 C60 106 40 98 18 93' })
  );

  var leaf3 = e('svg', { className: 'dash-leaf dash-leaf--3', viewBox: '0 0 120 180', xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': 'true' },
    e('defs', null, e('linearGradient', { id: 'lg3-a', x1: '0%', y1: '0%', x2: '100%', y2: '100%' }, e('stop', { offset: '0%', stopColor: '#55c89a' }), e('stop', { offset: '100%', stopColor: '#88e0bc' }))),
    e('path', { fill: 'url(#lg3-a)', d: 'M60 170 C38 145 12 115 6 82 C0 48 18 16 48 6 C54 3 60 2 68 4 C90 10 110 36 112 68 C118 104 104 142 82 162 Z' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.20)', strokeWidth: '2', strokeLinecap: 'round', d: 'M60 170 C59 128 57 90 55 58 C52 30 50 12 48 6' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.13)', strokeWidth: '1', strokeLinecap: 'round', d: 'M57 135 C45 127 30 120 14 116' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.13)', strokeWidth: '1', strokeLinecap: 'round', d: 'M58 105 C70 98 84 92 100 88' })
  );

  var leaf4 = e('svg', { className: 'dash-leaf dash-leaf--4', viewBox: '0 0 220 180', xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': 'true' },
    e('defs', null, e('linearGradient', { id: 'lg4-a', x1: '100%', y1: '0%', x2: '0%', y2: '100%' }, e('stop', { offset: '0%', stopColor: '#1e7a5e' }), e('stop', { offset: '100%', stopColor: '#3dac80' }))),
    e('path', { fill: 'url(#lg4-a)', d: 'M210 0 C195 30 170 55 145 70 C120 85 80 90 50 100 C25 108 5 120 0 140 C10 120 35 100 70 88 C105 76 148 68 172 50 C195 32 208 15 210 0 Z' }),
    e('path', { fill: 'rgba(30,122,94,0.55)', d: 'M220 10 C205 45 185 75 160 95 C135 115 100 125 75 138 C50 150 25 162 10 175 C30 158 62 142 95 128 C128 114 165 100 188 78 C210 56 220 30 220 10 Z' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.20)', strokeWidth: '2', strokeLinecap: 'round', d: 'M210 0 C190 35 162 62 130 80 C98 98 58 106 30 118 C15 124 3 132 0 140' })
  );

  var leaf5 = e('svg', { className: 'dash-leaf dash-leaf--5', viewBox: '0 0 160 120', xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': 'true' },
    e('defs', null, e('linearGradient', { id: 'lg5-a', x1: '100%', y1: '0%', x2: '0%', y2: '100%' }, e('stop', { offset: '0%', stopColor: '#3aaa82' }), e('stop', { offset: '100%', stopColor: '#6dcfa0' }))),
    e('path', { fill: 'url(#lg5-a)', d: 'M155 5 C140 28 118 46 94 58 C70 70 42 74 20 82 C8 86 0 92 0 100 C8 86 32 76 62 66 C92 56 124 46 144 28 C158 15 158 5 155 5 Z' }),
    e('path', { fill: 'none', stroke: 'rgba(255,255,255,0.18)', strokeWidth: '1.5', strokeLinecap: 'round', d: 'M155 5 C136 30 112 50 84 62 C56 74 24 80 0 100' })
  );

  return e('div', { className: 'dash-bg-leaves', 'aria-hidden': 'true' }, leaf1, leaf2, leaf3, leaf4, leaf5);
};

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   DROPLET ON LEAF ICON
   â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */


/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   MINI CALENDAR
   â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
var MiniCalendar = function (props) {
  var year = props.year, month = props.month, start = props.start, end = props.end;
  var hoverDate = props.hoverDate, onDayClick = props.onDayClick, onDayHover = props.onDayHover;
  var onPrev = props.onPrev, onNext = props.onNext;
  var showPrev = props.showPrev !== false, showNext = props.showNext !== false;
  var todayStr = toDateStr(new Date());
  var cells = buildCalendarCells(year, month);

  var isToday = function (d) { return d && toDateStr(d) === todayStr; };
  var isInRange = function (d) {
    if (!d) return false;
    var s = toDateStr(d);
    var rangeEnd = end || hoverDate || '';
    if (!start || !rangeEnd) return false;
    var lo = start < rangeEnd ? start : rangeEnd;
    var hi = start < rangeEnd ? rangeEnd : start;
    return s > lo && s < hi;
  };
  var isRangeStart = function (d) {
    if (!d) return false;
    var s = toDateStr(d);
    var rangeEnd = end || hoverDate || '';
    if (!start || !rangeEnd) return s === start;
    return s === (start < rangeEnd ? start : rangeEnd);
  };
  var isRangeEnd = function (d) {
    if (!d) return false;
    var s = toDateStr(d);
    var rangeEnd = end || hoverDate || '';
    if (!start || !rangeEnd) return false;
    return s === (start < rangeEnd ? rangeEnd : start);
  };

  var e = React.createElement;
  return e('div', { className: 'dc-cal' },
    e('div', { className: 'dc-cal-nav' },
      showPrev ? e('button', { type: 'button', className: 'dc-cal-nav-btn', onClick: onPrev }, '\u2039')
        : e('div', { style: { width: 28 } }),
      e('span', { className: 'dc-cal-month-label' }, MONTHS[month] + ' ' + year),
      showNext ? e('button', { type: 'button', className: 'dc-cal-nav-btn', onClick: onNext }, '\u203A')
        : e('div', { style: { width: 28 } })
    ),
    e('div', { className: 'dc-cal-weekdays' },
      WEEKDAYS.map(function (wd) { return e('div', { key: wd, className: 'dc-cal-weekday' }, wd); })
    ),
    e('div', { className: 'dc-cal-days' },
      cells.map(function (cell, idx) {
        if (!cell) return e('div', { key: 'b-' + idx, className: 'dc-cal-day is-empty' });
        var cls = 'dc-cal-day';
        if (isToday(cell)) cls += ' is-today';
        if (isRangeStart(cell)) cls += ' is-sel-start';
        if (isRangeEnd(cell)) cls += ' is-sel-end';
        if (isInRange(cell)) cls += ' is-in-range';
        return e('button', {
          key: toDateStr(cell), type: 'button', className: cls,
          onClick: function () { onDayClick && onDayClick(cell); },
          onMouseEnter: function () { onDayHover && onDayHover(toDateStr(cell)); },
        }, cell.getDate());
      })
    )
  );
};



/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   DATE FILTER POPUP
   Fixed position â€” always top-right,
   no page scroll, inner calendar scrolls
   â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
var DateFilterPopup = function (props) {
  var todayDate = new Date(), todayStr = toDateStr(todayDate);
  var startState = useState(props.initialStart || todayStr);
  var start = startState[0], setStart = startState[1];
  var endState = useState(props.initialEnd || todayStr);
  var end = endState[0], setEnd = endState[1];
  var presetState = useState(props.initialPreset || 'today');
  var preset = presetState[0], setPreset = presetState[1];
  var pickStepState = useState('start');
  var pickStep = pickStepState[0], setPickStep = pickStepState[1];
  var hoverState = useState('');
  var hoverDate = hoverState[0], setHoverDate = hoverState[1];
  var leftYearState = useState(todayDate.getFullYear());
  var leftYear = leftYearState[0], setLeftYear = leftYearState[1];
  var leftMonthState = useState(todayDate.getMonth() === 0 ? 11 : todayDate.getMonth() - 1);
  var leftMonth = leftMonthState[0], setLeftMonth = leftMonthState[1];
  var rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;
  var rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;

  var goLeft = function () {
    if (leftMonth === 0) { setLeftMonth(11); setLeftYear(function (y) { return y - 1; }); }
    else { setLeftMonth(function (m) { return m - 1; }); }
  };
  var goRight = function () {
    if (leftMonth === 11) { setLeftMonth(0); setLeftYear(function (y) { return y + 1; }); }
    else { setLeftMonth(function (m) { return m + 1; }); }
  };

  var applyPreset = function (key) {
    setPreset(key);
    var now = new Date(), s, e2;
    if (key === 'today') { s = toDateStr(now); e2 = toDateStr(now); }
    else if (key === 'yesterday') {
      var y = new Date(now); y.setDate(now.getDate() - 1); s = toDateStr(y); e2 = toDateStr(y);
    }
    else if (key === '7d') { var d7 = new Date(now); d7.setDate(now.getDate() - 6); s = toDateStr(d7); e2 = toDateStr(now); }
    else if (key === '30d') { var d30 = new Date(now); d30.setDate(now.getDate() - 29); s = toDateStr(d30); e2 = toDateStr(now); }
    else if (key === 'this_month') {
      var tm = new Date(now.getFullYear(), now.getMonth(), 1); s = toDateStr(tm); e2 = toDateStr(now);
    }
    else if (key === 'custom') { setStart(''); setEnd(''); setPickStep('start'); return; }
    setStart(s); setEnd(e2); setPickStep('start'); setHoverDate('');
  };

  var handleDayClick = function (dateObj) {
    if (!dateObj) return;
    var str = toDateStr(dateObj);
    setPreset('custom');
    if (pickStep === 'start' || (start && end)) {
      setStart(str); setEnd(''); setPickStep('end');
    } else {
      if (str < start) { setStart(str); setEnd(start); } else { setEnd(str); }
      setPickStep('start'); setHoverDate('');
    }
  };

  var handleApply = function () {
    var applyStart = start, applyEnd = end;
    if (start && !end) applyEnd = start;
    props.onApply(applyStart, applyEnd, preset);
  };

  var handleClear = function () {
    setStart(''); setEnd(''); setPreset('7d'); setPickStep('start'); setHoverDate('');
    props.onApply('', '', '7d');
  };

  var fmtDisp = function (s) {
    if (!s) return '';
    var d = new Date(s + 'T12:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  var exportRows = buildExportRows(props.orders || [], start, end);
  var noData = exportRows.length === 0;

  var presets = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7d', label: 'Last 7 Days' },
    { key: '30d', label: 'Last 30 Days' },
    { key: 'this_month', label: 'This Month' },
    { key: 'custom', label: 'Custom' },
  ];

  var e = React.createElement;

  return e('div', { className: 'dfd-overlay', onClick: props.onClose },
    /* Popup */
    e('div', {
      className: 'dfd-popup',
      onClick: function (ev) { ev.stopPropagation(); }
    },
      /* Header */
      e('div', { className: 'dfd-header' },
        e('div', null,
          e('div', { className: 'dfd-header-title' }, 'Filter & Export'),
          e('div', { className: 'dfd-header-sub' }, 'Select a date range to filter data')
        ),
        e('button', { type: 'button', className: 'dfd-close-btn', onClick: props.onClose }, '\u00D7')
      ),

      /* Body: scrollable calendars + fixed presets sidebar */
      e('div', { className: 'dfd-body' },

        /* Scrollable calendar area */
        e('div', { className: 'dfd-calendars' },
          /* Date range display */
          e('div', { className: 'dfd-range-display' },
            e('div', { className: 'dfd-range-field' + (pickStep === 'start' ? ' is-picking' : '') },
              e('div', { className: 'dfd-range-field-label' }, 'Start Date'),
              e('div', { className: 'dfd-range-field-value' }, start ? fmtDisp(start) : 'mm/dd/yyyy')
            ),
            e('div', { className: 'dfd-range-sep' },
              e('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                e('path', { d: 'M5 12h14M12 5l7 7-7 7' })
              )
            ),
            e('div', { className: 'dfd-range-field' + (pickStep === 'end' ? ' is-picking' : '') },
              e('div', { className: 'dfd-range-field-label' }, 'End Date'),
              e('div', { className: 'dfd-range-field-value' }, end ? fmtDisp(end) : 'mm/dd/yyyy')
            )
          ),

          /* Dual calendar */
          e('div', { className: 'dfd-dual-cal', onMouseLeave: function () { setHoverDate(''); } },
            e(MiniCalendar, {
              year: leftYear, month: leftMonth, start: start, end: end, hoverDate: hoverDate,
              onDayClick: handleDayClick,
              onDayHover: function (s) { if (pickStep === 'end' && start) setHoverDate(s); },
              onPrev: goLeft, onNext: null, showPrev: true, showNext: false
            }),
            e('div', { className: 'dfd-cal-divider' }),
            e(MiniCalendar, {
              year: rightYear, month: rightMonth, start: start, end: end, hoverDate: hoverDate,
              onDayClick: handleDayClick,
              onDayHover: function (s) { if (pickStep === 'end' && start) setHoverDate(s); },
              onPrev: null, onNext: goRight, showPrev: false, showNext: true
            })
          ),

          /* No data notice */
          (start || end) && noData
            ? e('div', { className: 'dfd-no-data-notice' },
              e('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                e('circle', { cx: 12, cy: 12, r: 10 }),
                e('line', { x1: 12, y1: 8, x2: 12, y2: 12 }),
                e('line', { x1: 12, y1: 16, x2: 12.01, y2: 16 })
              ),
              'No previous data available for this date range'
            )
            : null
        ),

        /* Presets sidebar â€” never scrolls */
        e('div', { className: 'dfd-presets' },
          e('div', { className: 'dfd-presets-title' }, 'Quick Select'),
          presets.map(function (p) {
            return e('button', {
              key: p.key, type: 'button',
              className: 'dfd-preset-btn' + (preset === p.key ? ' is-active' : ''),
              onClick: function () { applyPreset(p.key); }
            }, p.label);
          })
        )
      ),

      /* Footer â€” always pinned to bottom */
      e('div', { className: 'dfd-footer' },
        e('button', {
          type: 'button', className: 'dfd-export-btn',
          onClick: function () { downloadExcel(exportRows, 'orders-' + (start || 'all') + '.xlsx'); }
        },
          e('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
            e('path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }),
            e('polyline', { points: '7 10 12 15 17 10' }),
            e('line', { x1: 12, y1: 15, x2: 12, y2: 3 })
          ),
          'Download Excel'
        ),
        e('div', { className: 'dfd-action-btns' },
          e('button', { type: 'button', className: 'dfd-clear-btn', onClick: handleClear }, 'Clear'),
          e('button', {
            type: 'button', className: 'dfd-apply-btn',
            onClick: handleApply,
            disabled: !start && !end
          },
            e('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
              e('polyline', { points: '20 6 9 17 4 12' })
            ),
            'Apply'
          )
        )
      )
    )
  );
};

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   REVENUE CHART â€” REDESIGNED
   Clean bar chart with smooth curve overlay
   Matches the reference image style
   â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
var RevenueChart = function (props) {
  var series = props.series || [];
  var chartDays = props.chartDays || 7;
  var onToggleDays = props.onToggleDays;
  var filterLabel = props.filterLabel || '';
  var previousSeries = props.previousSeries || [];

  var tooltipState = useState(null);
  var tooltip = tooltipState[0], setTooltip = tooltipState[1];

  /* SVG dimensions */
  var W = 560, H = 220, PL = 52, PR = 16, PT = 18, PB = 40;
  var innerW = W - PL - PR;
  var innerH = H - PT - PB;

  /* Colors */
  var CURRENT_COLOR = '#0d9488';
  var PREVIOUS_COLOR = '#1e3a8a';
  var PREVIOUS_MUTED = 'rgba(30,58,138,0.58)';
  var GRID_LINE_COLOR = 'rgba(30,58,138,0.10)';

  var maxVal = useMemo(function () {
    var m = series.reduce(function (acc, r) { return Math.max(acc, Number(r.value) || 0); }, 0);
    m = previousSeries.reduce(function (acc, r) { return Math.max(acc, Number(r.value) || 0); }, m);
    return Math.max(m, 1);
  }, [series, previousSeries]);

  var yGuideRatios = [0, 0.5, 1];

  /* Convert series to SVG points */
  var toPoints = function (src) {
    var count = src.length;
    return src.map(function (it, idx) {
      var rx = count > 1 ? idx / (count - 1) : 0.5;
      var ry = (Number(it.value) || 0) / maxVal;
      return {
        x: PL + innerW * rx,
        y: PT + innerH * (1 - ry),
        value: Number(it.value) || 0,
        day: it.day || '',
        weekDay: it.weekDay || '',
        raw: ry,
      };
    });
  };

  /* Smooth curve path with clamped control points for cleaner peaks */
  var makeSmoothLine = function (pts) {
    if (!pts.length) return '';
    if (pts.length === 1) return 'M ' + pts[0].x + ' ' + pts[0].y;
    var d = 'M ' + pts[0].x + ' ' + pts[0].y;
    for (var i = 0; i < pts.length - 1; i++) {
      var p0 = pts[i - 1] || pts[i];
      var p1 = pts[i];
      var p2 = pts[i + 1];
      var p3 = pts[i + 2] || p2;
      var tension = 0.9;
      var cp1x = p1.x + ((p2.x - p0.x) / 6) * tension;
      var cp1y = p1.y + ((p2.y - p0.y) / 6) * tension;
      var cp2x = p2.x - ((p3.x - p1.x) / 6) * tension;
      var cp2y = p2.y - ((p3.y - p1.y) / 6) * tension;
      var minY = Math.min(p1.y, p2.y);
      var maxY = Math.max(p1.y, p2.y);
      cp1y = Math.min(maxY, Math.max(minY, cp1y));
      cp2y = Math.min(maxY, Math.max(minY, cp2y));
      d += ' C ' + cp1x + ' ' + cp1y + ' ' + cp2x + ' ' + cp2y + ' ' + p2.x + ' ' + p2.y;
    }
    return d;
  };

  /* Area fill under the curve */
  var makeAreaPath = function (pts) {
    if (!pts.length) return '';
    var line = makeSmoothLine(pts);
    return line + ' L ' + pts[pts.length - 1].x + ' ' + (PT + innerH) + ' L ' + pts[0].x + ' ' + (PT + innerH) + ' Z';
  };

  var points = useMemo(function () { return toPoints(series); }, [series, maxVal]);
  var prevPoints = useMemo(function () { return toPoints(previousSeries); }, [previousSeries, maxVal]);
  var linePath = useMemo(function () { return makeSmoothLine(points); }, [points]);
  var areaPath = useMemo(function () { return makeAreaPath(points); }, [points]);
  var prevLine = useMemo(function () { return makeSmoothLine(prevPoints); }, [prevPoints]);

  /* Hit zone width per data point */
  var hitW = points.length > 1 ? innerW / (points.length - 1) : innerW;

  var e = React.createElement;

  return e('div', { className: 'rc-card' },

    /* Header row */
    e('div', { className: 'rc-header' },
      e('div', null,
        e('div', { className: 'rc-title' }, 'Revenue Overview'),
        e('div', { className: 'rc-subtitle' }, filterLabel || ('Last ' + chartDays + ' days'))
      ),
      e('div', { className: 'rc-header-right' },
        /* Legend */
        e('div', { className: 'rc-legend' },
          e('div', { className: 'rc-legend-item' },
            e('div', { className: 'rc-legend-dot', style: { background: CURRENT_COLOR } }),
            'Current'
          ),
          previousSeries.length > 0
            ? e('div', { className: 'rc-legend-item' },
              e('div', { className: 'rc-legend-line', style: { color: PREVIOUS_MUTED, borderTopColor: PREVIOUS_MUTED } }),
              'Previous'
            )
            : null
        ),
        /* Day toggles */
        e('div', { className: 'rc-toggle-group' },
          e('button', { type: 'button', className: 'rc-toggle-btn' + (chartDays === 7 ? ' active' : ''), onClick: function () { onToggleDays(7); } }, '7D'),
          e('button', { type: 'button', className: 'rc-toggle-btn' + (chartDays === 30 ? ' active' : ''), onClick: function () { onToggleDays(30); } }, '30D'),
          e('button', { type: 'button', className: 'rc-toggle-btn' + (chartDays === 90 ? ' active' : ''), onClick: function () { onToggleDays(90); } }, '90D'),
          e('button', { type: 'button', className: 'rc-toggle-btn' + (chartDays === 180 ? ' active' : ''), onClick: function () { onToggleDays(180); } }, '6M'),
          e('button', { type: 'button', className: 'rc-toggle-btn' + (chartDays === 365 ? ' active' : ''), onClick: function () { onToggleDays(365); } }, '1Y')
        )
      )
    ),

    /* Chart SVG */
    e('div', { className: 'rc-chart-area', style: { position: 'relative' } },
      e('svg', {
        className: 'rc-svg',
        viewBox: '0 0 ' + W + ' ' + H,
        preserveAspectRatio: 'xMidYMid meet',
        style: { width: '100%', height: 'auto', display: 'block', overflow: 'visible' }
      },
        e('defs', null,
          /* Gradient for current area fill */
          e('linearGradient', { id: 'rcAreaGrad2', x1: '0', y1: '0', x2: '0', y2: '1' },
            e('stop', { offset: '0%', stopColor: CURRENT_COLOR, stopOpacity: '0.12' }),
            e('stop', { offset: '55%', stopColor: CURRENT_COLOR, stopOpacity: '0.04' }),
            e('stop', { offset: '100%', stopColor: PREVIOUS_COLOR, stopOpacity: '0' })
          ),
          /* Clip path to keep area inside chart */
          e('clipPath', { id: 'rcClip' },
            e('rect', { x: PL, y: PT, width: innerW, height: innerH + 1 })
          )
        ),

        /* Minimal horizontal guide lines */
        yGuideRatios.map(function (ratio, idx) {
          var y = PT + innerH - (innerH * ratio);
          var isBaseLine = idx === 0;
          return e('line', {
            key: 'yg-' + idx,
            x1: PL, y1: y, x2: W - PR, y2: y,
            stroke: GRID_LINE_COLOR,
            strokeWidth: isBaseLine ? 1.2 : 0.8,
            strokeDasharray: isBaseLine ? 'none' : '3 10'
          });
        }),

        /* Previous period dashed line (behind current) */
        prevLine && previousSeries.length > 0
          ? e('path', {
            d: prevLine,
            fill: 'none',
            stroke: PREVIOUS_MUTED,
            strokeWidth: 2,
            strokeDasharray: '7 6',
            opacity: 1,
            clipPath: 'url(#rcClip)'
          })
          : null,

        /* Current area fill */
        areaPath
          ? e('path', {
            d: areaPath,
            fill: 'url(#rcAreaGrad2)',
            clipPath: 'url(#rcClip)'
          })
          : null,

        /* Current line â€” animated draw */
        linePath
          ? e('path', {
            d: linePath,
            fill: 'none',
            stroke: CURRENT_COLOR,
            strokeWidth: 2.4,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            className: 'rc-line-anim',
            clipPath: 'url(#rcClip)'
          })
          : null,

        /* Interactive hit zones + dots */
        points.map(function (p, idx) {
          var isHovered = tooltip && tooltip.idx === idx;
          return e('g', { key: 'pt-' + idx },
            /* Transparent hit zone */
            e('rect', {
              x: p.x - hitW / 2,
              y: PT,
              width: hitW,
              height: innerH,
              fill: 'transparent',
              style: { cursor: 'crosshair' },
              onMouseEnter: function () { setTooltip({ idx: idx, x: p.x, y: p.y, value: p.value, day: p.day, weekDay: p.weekDay }); },
              onMouseLeave: function () { setTooltip(null); }
            }),
            /* Vertical crosshair on hover */
            isHovered
              ? e('line', {
                x1: p.x, y1: PT, x2: p.x, y2: PT + innerH,
                stroke: PREVIOUS_COLOR, strokeWidth: 1,
                strokeDasharray: '3 5', opacity: 0.24
              })
              : null,
            /* Data dot */
            e('circle', {
              cx: p.x, cy: p.y,
              r: isHovered ? 5 : (p.value > 0 ? 3.1 : 2.3),
              fill: p.value > 0 ? '#ffffff' : 'rgba(148,163,184,0.30)',
              stroke: p.value > 0 ? CURRENT_COLOR : 'rgba(148,163,184,0.45)',
              strokeWidth: isHovered ? 2.8 : 2,
              style: { transition: 'r 0.12s ease, fill 0.12s ease' }
            })
          );
        }),

        /* X-axis labels (day + weekday) */
        points.map(function (p, idx) {
          /* Show fewer labels when many data points */
          var skip = points.length > 12 ? Math.ceil(points.length / 12) : 1;
          if (idx % skip !== 0 && idx !== points.length - 1) return null;
          return e('g', { key: 'xl-' + idx },
            e('text', {
              x: p.x, y: H - 20,
              textAnchor: 'middle',
              fontSize: 9,
              fill: '#94a3b8',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 600
            }, p.day || ''),
            p.weekDay
              ? e('text', {
                x: p.x, y: H - 8,
                textAnchor: 'middle',
                fontSize: 8,
                fill: '#cbd5e1',
                fontFamily: 'DM Sans, sans-serif',
                fontWeight: 500
              }, p.weekDay)
              : null
          );
        })
      ),

      /* Tooltip */
      tooltip
        ? e('div', {
          className: 'rc-tooltip',
          style: {
            left: Math.min(Math.max((tooltip.x / W) * 100, 8), 88) + '%',
            top: Math.max(tooltip.y - 50, 4) + 'px',
          }
        },
          e('div', { className: 'rc-tooltip-value' }, formatINR(tooltip.value)),
          e('div', { className: 'rc-tooltip-day' }, tooltip.day + (tooltip.weekDay ? ' - ' + tooltip.weekDay : ''))
        )
        : null
    )
  );
};


var OrdersBarChart = function (props) {
  var series = props.series || [];
  var chartDays = props.chartDays || 7;
  var onToggleDays = props.onToggleDays;
  var filterLabel = props.filterLabel || '';
  var maxVal = useMemo(function () {
    return Math.max(series.reduce(function (acc, r) { return Math.max(acc, Number(r.value) || 0); }, 0), 1);
  }, [series]);

  var e = React.createElement;
  var barW = series.length > 0 ? Math.max(6, Math.min(28, 300 / series.length)) : 20;

  return e('div', { className: 'oc-card' },
    e('div', { className: 'oc-header' },
      e('div', null,
        e('div', { className: 'oc-title' }, 'Orders Overview'),
        e('div', { className: 'oc-subtitle' }, filterLabel || ('Last ' + chartDays + ' days'))
      ),
      e('div', { className: 'rc-toggle-group oc-toggle-group' },
        e('button', { type: 'button', className: 'rc-toggle-btn' + (chartDays === 7 ? ' active' : ''), onClick: function () { onToggleDays && onToggleDays(7); } }, '7D'),
        e('button', { type: 'button', className: 'rc-toggle-btn' + (chartDays === 30 ? ' active' : ''), onClick: function () { onToggleDays && onToggleDays(30); } }, '30D'),
        e('button', { type: 'button', className: 'rc-toggle-btn' + (chartDays === 90 ? ' active' : ''), onClick: function () { onToggleDays && onToggleDays(90); } }, '90D'),
        e('button', { type: 'button', className: 'rc-toggle-btn' + (chartDays === 180 ? ' active' : ''), onClick: function () { onToggleDays && onToggleDays(180); } }, '6M'),
        e('button', { type: 'button', className: 'rc-toggle-btn' + (chartDays === 365 ? ' active' : ''), onClick: function () { onToggleDays && onToggleDays(365); } }, '1Y')
      )
    ),
    e('div', { className: 'oc-bars-wrap' },
      series.length === 0
        ? e('div', { className: 'dash-empty oc-empty' }, 'No order data available.')
        : series.map(function (row, idx) {
          var heightPct = (row.value / maxVal) * 100;
          return e('div', { key: row.key || idx, className: 'oc-bar-col', title: row.day + ': ' + row.value + ' orders' },
            e('div', { className: 'oc-bar-track' },
              e('div', { className: 'oc-bar-fill', style: { height: heightPct + '%', width: barW + 'px' } })
            ),
            e('div', { className: 'oc-bar-label' }, row.value),
            e('div', { className: 'oc-bar-day' }, row.day)
          );
        })
    )
  );
};
var Dashboard = function () {
  var navigate = useNavigate();
  var filterBtnRef = useRef(null);
  var pageRef = useRef(null);

  var productCountState = useState(0); var productCount = productCountState[0]; var setProductCount = productCountState[1];
  var productsState = useState([]); var products = productsState[0]; var setProducts = productsState[1];
  var customersState = useState([]); var customers = customersState[0]; var setCustomers = customersState[1];
  var customerCountState = useState(0); var customerCount = customerCountState[0]; var setCustomerCount = customerCountState[1];
  var allOrdersState = useState([]); var allOrders = allOrdersState[0]; var setAllOrders = allOrdersState[1];
  var revenueDataState = useState({ todayRevenue: 0, last7Days: [] });
  var revenueData = revenueDataState[0]; var setRevenueData = revenueDataState[1];
  var loadedState = useState(false); var loaded = loadedState[0]; var setLoaded = loadedState[1];
  var dataErrorState = useState(''); var dataError = dataErrorState[0]; var setDataError = dataErrorState[1];

  var showFilterState = useState(false); var showFilter = showFilterState[0]; var setShowFilter = showFilterState[1];
  var filterStartState = useState(''); var filterStart = filterStartState[0]; var setFilterStart = filterStartState[1];
  var filterEndState = useState(''); var filterEnd = filterEndState[0]; var setFilterEnd = filterEndState[1];
  var filterPresetState = useState('today'); var filterPreset = filterPresetState[0]; var setFilterPreset = filterPresetState[1];
  var chartDaysState = useState(7); var chartDays = chartDaysState[0]; var setChartDays = chartDaysState[1];

  /* ESC to close */
  useEffect(function () {
    if (!showFilter) return;
    var handler = function (ev) { if (ev.key === 'Escape') setShowFilter(false); };
    document.addEventListener('keydown', handler);
    return function () { document.removeEventListener('keydown', handler); };
  }, [showFilter]);

  /* Simple toggle â€” popup uses fixed CSS positioning, no JS position calc needed */
  var handleFilterToggle = useCallback(function () {
    setShowFilter(function (v) { return !v; });
  }, []);

  var loadDashboardData = function (summaryFrom, summaryTo) {
    return Promise.allSettled([
      getAllProductsUnpaginated(),
      getAllOrders(summaryFrom, summaryTo),
      getAllCustomers(),
      getDashboardSummary(summaryFrom, summaryTo)
    ]).then(function (results) {
      var prods = results[0].status === 'fulfilled' ? extractProductArray(results[0].value) : [];
      var ordersList = results[1].status === 'fulfilled' ? extractOrderArray(results[1].value) : [];
      var custs = results[2].status === 'fulfilled' ? extractCustomerArray(results[2].value) : [];
      var activeOrders = Array.isArray(ordersList) ? ordersList.filter(function (o) { return o && !o.isDeleted; }) : [];
      var activeCustomers = Array.isArray(custs) ? custs.filter(function (c) { return c && !c.isDeleted; }) : [];
      var summaryRes = results[3].status === 'fulfilled' ? results[3].value : null;
      var revenuePayload = { todayRevenue: 0, last7Days: [], totalAmount: 0 };
      if (summaryRes && summaryRes.data) {
        var raw = summaryRes.data;
        if (raw && raw.success && raw.data) raw = raw.data;
        else if (raw && raw.data && typeof raw.data === 'object') raw = raw.data;
        revenuePayload.todayRevenue = parseAmount(raw.todayRevenue || raw.today_revenue || 0);
        revenuePayload.last7Days = Array.isArray(raw.graphData) ? raw.graphData : (Array.isArray(raw.last7Days) ? raw.last7Days : []);
        revenuePayload.totalAmount = parseAmount(raw.totalAmount || raw.totalRevenue || raw.total || raw.amount || 0);
      }
      setProducts(prods);
      setCustomers(activeCustomers);
      setProductCount(Array.isArray(prods) ? prods.length : 0);
      setCustomerCount(activeCustomers.length);
      setAllOrders(activeOrders);
      setDataError(results.some(function (r) { return r.status === 'rejected'; }) ? 'Some dashboard data could not be refreshed. Showing available live data.' : '');
      setRevenueData(revenuePayload);
      setLoaded(true);
    }).catch(function () {
      setProducts([]); setCustomers([]); setProductCount(0); setCustomerCount(0); setAllOrders([]);
      setRevenueData({ todayRevenue: 0, last7Days: [] });
      setDataError('Unable to load dashboard data. Please try again.');
      setLoaded(true);
    });
  };

  useEffect(function () {
    var range = getDefaultSummaryRange();
    loadDashboardData(range.from, range.to);
    var onAdminDataUpdated = function () {
      var r = getDefaultSummaryRange();
      loadDashboardData(r.from, r.to);
    };
    window.addEventListener('adminDataUpdated', onAdminDataUpdated);
    return function () { window.removeEventListener('adminDataUpdated', onAdminDataUpdated); };
  }, []);

  /* Reload data when chartDays changes (for 7D, 30D, etc. buttons) */
  useEffect(function () {
    if (!filterStart && !filterEnd) {
      var now = new Date();
      var from = new Date(now);
      from.setDate(now.getDate() - (chartDays - 1));
      var fromStr = toDateStr(from);
      var toStr = toDateStr(now);
      loadDashboardData(fromStr, toStr);
    }
  }, [chartDays]);

  var filteredOrders = useMemo(function () {
    return filterOrdersByRange(allOrders, filterStart, filterEnd);
  }, [allOrders, filterStart, filterEnd]);

  var filterRangeDays = useMemo(function () {                      // ← new block
    if (!filterStart || !filterEnd) return null;
    var start = startOfDay(filterStart);
    var end = startOfDay(filterEnd);
    var spanDays = Math.round((end - start) / 86400000) + 1;
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var offsetDays = Math.round((today - end) / 86400000);
    if (offsetDays < 0) offsetDays = 0;
    return { spanDays: spanDays, offsetDays: offsetDays };
  }, [filterStart, filterEnd]);

  var filteredProducts = useMemo(function () {
    return products;
  }, [products]);

  var filteredCustomers = useMemo(function () {
    return filterOrdersByRange(customers, filterStart, filterEnd);
  }, [customers, filterStart, filterEnd]);

  var totalAmount = useMemo(function () {
    return filteredOrders
      .filter(function (o) { return getOrderStatus(o) !== 'cancelled'; })
      .reduce(function (s, o) { return s + getOrderTotal(o); }, 0);
  }, [filteredOrders]);

  var apiRevenueSeries = useMemo(function () {
    if (!revenueData || !Array.isArray(revenueData.last7Days)) return [];
    var rows = revenueData.last7Days.map(function (item) {
      if (item && item.date && typeof item.date === 'string') {
        var parts = item.date.split('-');
        var mo = parts.length > 1 ? Number(parts[1]) - 1 : null;
        var dy = parts.length > 2 ? Number(parts[2]) : null;
        var label = dy !== null && mo !== null
          ? String(dy).padStart(2, '0') + ' ' + ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][mo]
          : item.date;
        /* Get weekday */
        var weekDay = '';
        if (dy !== null && mo !== null) {
          var dateObj = new Date(Number(parts[0]), mo, dy);
          weekDay = dateObj.toLocaleDateString('en-IN', { weekday: 'short' });
        }
        return { key: item.date, day: label, weekDay: weekDay, value: Number(item.amount) || 0, raw: item, month: mo || 0, dayOfMonth: dy || 0 };
      }
      var day = item && item._id && item._id.day !== undefined ? Number(item._id.day) : null;
      var month = item && item._id && item._id.month !== undefined ? Number(item._id.month) : null;
      var fallbackLabel = day !== null && month !== null
        ? String(day).padStart(2, '0') + '/' + String(month).padStart(2, '0')
        : String((item && item._id && item._id.date) || (item && item.label) || '');
      return { key: fallbackLabel, day: fallbackLabel, weekDay: '', value: Number(item && item.amount) || 0, raw: item, month: month || 0, dayOfMonth: day || 0 };
    });
    rows.sort(function (a, b) { return a.month !== b.month ? a.month - b.month : a.dayOfMonth - b.dayOfMonth; });
    return rows;
  }, [revenueData]);

  var revenueSeries = useMemo(function () {
    if (!filterStart && !filterEnd && chartDays === 7 && apiRevenueSeries.length > 0) return apiRevenueSeries;
    if (filterRangeDays) {                                            // ← new
      return buildRevenueSeries(filteredOrders, filterRangeDays.spanDays, filterRangeDays.offsetDays);
    }
    var ordersForSeries = (filterStart || filterEnd) ? filteredOrders : allOrders;
    return buildRevenueSeries(ordersForSeries, chartDays, 0);
  }, [filteredOrders, allOrders, chartDays, apiRevenueSeries, filterStart, filterEnd, filterRangeDays]);

  var previousRevenueSeries = useMemo(function () {
    if (!filterStart && !filterEnd && chartDays === 7 && apiRevenueSeries.length > 0) return [];
    if (filterRangeDays) {                                            // ← new
      return buildRevenueSeries(allOrders, filterRangeDays.spanDays, filterRangeDays.offsetDays + filterRangeDays.spanDays);
    }
    return buildRevenueSeries(allOrders, chartDays, chartDays);
  }, [allOrders, chartDays, filterStart, filterEnd, apiRevenueSeries, filterRangeDays]);

  var dayRevenueSeries = useMemo(function () {
    if (!filterStart && !filterEnd && chartDays === 7 && apiRevenueSeries.length > 0) return apiRevenueSeries;
    return revenueSeries;
  }, [filterStart, filterEnd, chartDays, apiRevenueSeries, revenueSeries]);

  var maxDayRevenue = useMemo(function () {
    return Math.max(dayRevenueSeries.reduce(function (max, row) { return Math.max(max, Number(row.value) || 0); }, 0), 1);
  }, [dayRevenueSeries]);

  var topProducts = useMemo(function () { return buildTopProducts(filteredOrders); }, [filteredOrders]);

  var orderCountSeries = useMemo(function () {
    if (filterRangeDays) {                                            // ← new
      return buildOrderCountSeries(filteredOrders, filterRangeDays.spanDays, filterRangeDays.offsetDays);
    }
    var ordersForSeries = (filterStart || filterEnd) ? filteredOrders : allOrders;
    return buildOrderCountSeries(ordersForSeries, chartDays, 0);
  }, [filteredOrders, allOrders, chartDays, filterStart, filterEnd, filterRangeDays]);

  var handleFilterApply = function (start, end, preset) {
    if (start && !end) end = start;
    setFilterStart(start); setFilterEnd(end); setFilterPreset(preset); setShowFilter(false);
    var from = start || '', to = end || '';
    if (!from && !to) { var range = getDefaultSummaryRange(); from = range.from; to = range.to; }
    loadDashboardData(from, to);
  };

  var handleFilterClear = function () {
    setFilterStart(''); setFilterEnd(''); setFilterPreset('7d');
    var range = getDefaultSummaryRange();
    loadDashboardData(range.from, range.to);
  };

  var handleSelectedRangeExport = function () {
    downloadExcel(buildExportRows(allOrders, filterStart, filterEnd), 'orders-' + (filterStart || 'all') + '.xlsx');
  };

  var filterLabel = useMemo(function () {
    if (!filterStart && !filterEnd) return '';
    var fmt = function (s) { return s ? new Date(s + 'T12:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''; };
    return fmt(filterStart) + (filterEnd && filterEnd !== filterStart ? ' \u2013 ' + fmt(filterEnd) : '');
  }, [filterStart, filterEnd]);

  /* Stat cards */
  var stats = [
    {
      cardClass: 'dash-card--revenue',
      val: formatINR(totalAmount),
      lbl: 'TOTAL REVENUE',
      growthText: '12.5% from last 7 days',
      path: '/admin/payments',
      iconContent: '\u20B9',
      isText: true,
      sparkPath: 'M 0,38 Q 40,32 80,35 T 160,18 T 200,10',
      endPt: { cx: 200, cy: 10 }
    },
    {
      cardClass: 'dash-card--orders',
      val: String(filteredOrders.length),
      lbl: 'TOTAL ORDERS',
      growthText: '8.3% from last 7 days',
      path: '/admin/orders',
      iconPath: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 14H5V5h14v12z',
      isText: false,
      sparkPath: 'M 0,32 C 30,38 60,15 90,28 C 120,40 150,20 200,12',
      endPt: { cx: 200, cy: 12 }
    },
    {
      cardClass: 'dash-card--customers',
      val: String((filterStart || filterEnd) ? filteredCustomers.length : customerCount),
      lbl: 'CUSTOMERS',
      growthText: '5.2% from last 7 days',
      path: '/admin/customers',
      iconPath: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
      isText: false,
      sparkPath: 'M 0,35 Q 50,20 100,28 T 200,14',
      endPt: { cx: 200, cy: 14 }
    },
    {
      cardClass: 'dash-card--products',
      val: String(products.length),
      lbl: 'PRODUCTS',
      growthText: '3.1% from last 7 days',
      path: '/admin/products',
      iconPath: 'M20 6h-2.18c.07-.44.18-.88.18-1.35C18 2.51 16.5 1 14.65 1c-1.07 0-1.93.57-2.65 1.29L12 2.29l-.2-.2C11.07 1.37 10.21.8 9.35.8 7.5.8 6 2.31 6 4.15c0 .47.11.91.18 1.35H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z',
      isText: false,
      sparkPath: 'M 0,36 C 40,25 80,38 120,20 C 160,10 180,22 200,8',
      endPt: { cx: 200, cy: 8 }
    },
  ];

  var e = React.createElement;

  return e(AdminLayout, null,
    e(LeafBackground),

    e('div', { ref: pageRef, className: 'dash-page' + (loaded ? ' is-loaded' : '') },

      /* ── Header ── */
      e('div', { className: 'dash-header' },
        e('div', { className: 'dash-title-block' },
          e('div', { className: 'dash-title-headline' },
            e('div', { className: 'dash-crown-icon' },
              e('svg', {
                width: '38', height: '38', viewBox: '0 0 24 24', fill: 'none',
                xmlns: 'http://www.w3.org/2000/svg', 'aria-hidden': 'true'
              },
                e('rect', { x: '3', y: '3', width: '8', height: '8', rx: '2', stroke: '#d99a2b', strokeWidth: '2' }),
                e('rect', { x: '13', y: '3', width: '8', height: '8', rx: '2', stroke: '#061a38', strokeWidth: '2' }),
                e('rect', { x: '3', y: '13', width: '8', height: '8', rx: '2', stroke: '#061a38', strokeWidth: '2' }),
                e('path', { d: 'M14 18l2 2 5-6', stroke: '#d99a2b', strokeWidth: '2.4', strokeLinecap: 'round', strokeLinejoin: 'round' })
              )
            ),

            e('div', null,
              e('h1', { className: 'dash-title' }, 'Dashboard'),
              e('p', { className: 'dash-subtitle' }, '💎 Welcome Back, Admin! Let’s make today shine. ✨')
            )
          )
        ),

        e('div', { className: 'dash-header-actions' },
          dataError ? e('div', { className: 'dash-error' }, dataError) : null,
          filterLabel
            ? e('div', { className: 'dash-filter-range' },
              e('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
                e('rect', { x: 3, y: 4, width: 18, height: 18, rx: 2 }),
                e('line', { x1: 16, y1: 2, x2: 16, y2: 6 }),
                e('line', { x1: 8, y1: 2, x2: 8, y2: 6 }),
                e('line', { x1: 3, y1: 10, x2: 21, y2: 10 })
              ),
              filterLabel,
              e('button', { className: 'dash-filter-range-export', type: 'button', onClick: handleSelectedRangeExport, title: 'Export selected date range' }, 'Export'),
              e('button', { className: 'dash-filter-range-clear', type: 'button', onClick: handleFilterClear, title: 'Clear filter' }, '\u00D7')
            )
            : null,

          e('div', { className: 'dash-filter-wrapper' },
            e('button', {
              ref: filterBtnRef,
              type: 'button',
              className: 'dash-filter-btn' + (showFilter ? ' is-active' : ''),
              onClick: handleFilterToggle,
            },
              e('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                e('polygon', { points: '22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3' })
              ),
              showFilter ? 'Close' : 'Filter & Export'
            )
          )
        )
      ),

      /* ── Stat Cards ── */
      e('div', { className: 'dash-cards-grid' },
        stats.map(function (s, i) {
          return e('div', {
            key: i,
            className: 'dash-card ' + s.cardClass,
            onClick: function () { if (s.path) navigate(s.path); },
            style: {
              '--card-color': s.color,
              '--card-bg': s.bg,
              '--card-surface': s.surface || s.bg,
              animationDelay: (0.05 + i * 0.07) + 's',
            }
          },
            e('div', { className: 'dash-card-top' },
              e('div', { className: 'dash-card-icon' },
                s.isText
                  ? e('span', { style: { fontSize: 18, fontWeight: 800, color: '#ffffff', lineHeight: 1 } }, s.iconContent)
                  : e('svg', { viewBox: '0 0 24 24', style: { fill: '#ffffff', width: 20, height: 20 } },
                    e('path', { d: s.iconPath })
                  )
              ),
              e('div', { className: 'dash-card-info' },
                e('div', { className: 'dash-card-lbl' }, s.lbl),
                e('div', { className: 'dash-card-val' }, s.val)
              )
            ),
            e('div', { className: 'dash-card-bottom' },
              e('div', { className: 'dash-card-growth' },
                e('span', { className: 'dash-growth-arrow' }, '\u2197'),
                e('span', null, s.growthText)
              )
            ),
            e('div', { className: 'dash-card-watermark' },
              s.isText
                ? e('span', null, s.iconContent)
                : e('svg', { viewBox: '0 0 24 24' }, e('path', { d: s.iconPath }))
            ),
            e('svg', { className: 'dash-card-sparkline', viewBox: '0 0 200 45', preserveAspectRatio: 'none' },
              e('path', { d: s.sparkPath, fill: 'none', stroke: 'rgba(255, 255, 255, 0.35)', strokeWidth: '2.5', strokeLinecap: 'round' }),
              e('circle', { cx: s.endPt.cx, cy: s.endPt.cy, r: '3.5', fill: '#ffffff' })
            )
          );
        })
      ),

      /* â”€â”€ Main 2-column grid â”€â”€ */
      e('div', { className: 'dash-main-grid' },
        e('div', { className: 'dash-left-col' },
          e(RevenueChart, {
            series: revenueSeries,
            previousSeries: previousRevenueSeries,
            chartDays: chartDays,
            onToggleDays: function (d) { setChartDays(d); },
            filterLabel: filterLabel,
          }),
          e(OrdersBarChart, { series: orderCountSeries, chartDays: chartDays, onToggleDays: function (d) { setChartDays(d); }, filterLabel: filterLabel })
        ),
        e('div', { className: 'dash-right-col' },

          /* Top Products */
          e('div', { className: 'dash-widget' },
            e('div', { className: 'dash-widget-header' },
              e('div', null,
                e('div', { className: 'dash-widget-title' }, 'Top Products'),
                e('div', { className: 'dash-widget-sub' }, 'by sales \u00B7 top 5')
              )
            ),
            e('div', { className: 'tp-list' },
              topProducts.length === 0
                ? e('div', { className: 'dash-empty' }, 'No sales data yet.')
                : topProducts.map(function (p, idx) {
                  return e('div', {
                    key: p.r, className: 'tp-row',
                    style: { animationDelay: (0.10 + idx * 0.06) + 's', cursor: 'pointer' },
                    onClick: function () { navigate('/admin/products'); }
                  },
                    e('div', { className: 'tp-rank-badge ' + p.rc }, p.r),
                    e('div', { className: 'tp-info' },
                      e('div', { className: 'tp-name' }, p.n),
                      e('div', { className: 'tp-count' }, p.c)
                    ),
                    e('div', { className: 'tp-revenue' }, p.v)
                  );
                })
            )
          ),

          /* Day-wise Revenue */
          e('div', { className: 'dash-widget' },
            e('div', { className: 'dash-widget-header' },
              e('div', null,
                e('div', { className: 'dash-widget-title' }, 'Day-wise Revenue'),
                e('div', { className: 'dash-widget-sub' }, 'daily breakdown')
              )
            ),
            dayRevenueSeries.length === 0
              ? e('div', { className: 'dash-empty' }, 'No revenue data available.')
              : e('div', { className: 'dw-list' },
                dayRevenueSeries.map(function (row, idx) {
                  return e('div', {
                    key: row.key || idx, className: 'dw-row',
                    style: { animationDelay: (0.05 + idx * 0.04) + 's' }
                  },
                    e('div', { className: 'dw-indicator' }),
                    e('span', { className: 'dw-label' }, row.day),
                    e('div', { className: 'dw-bar-wrap' },
                      e('div', { className: 'dw-bar dw-bar--full', style: { width: Math.max(3, ((Number(row.value) || 0) / maxDayRevenue) * 100) + '%', animationDelay: (0.12 + idx * 0.04) + 's' } })
                    ),
                    e('span', { className: 'dw-val' }, formatINR(row.value))
                  );
                })
              )
          )
        )
      )
    ),

    /* Filter Popup â€” rendered outside dash-page */
    showFilter
      ? e(DateFilterPopup, {
        onClose: function () { setShowFilter(false); },
        onApply: handleFilterApply,
        initialStart: filterStart,
        initialEnd: filterEnd,
        initialPreset: filterPreset,
        orders: allOrders,
      })
      : null
  );
};

export default Dashboard;